import { forwardRef } from 'react';
import { motion } from 'motion/react';
import { cn } from './cn';

export type CardVariant = 'elevated' | 'outlined' | 'plain' | 'glass';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual surface treatment. elevated = shadow + border; outlined = border only; plain = bare. */
  variant?: CardVariant;
  /** Adds pointer affordance + hover lift. Use for clickable cards/rows. */
  interactive?: boolean;
  /** Inner padding scale. */
  padding?: CardPadding;
}

const base = 'rounded-2xl bg-surface';

const variants: Record<CardVariant, string> = {
  elevated: 'shadow-card border border-line',
  outlined: 'border border-line',
  plain: '',
  glass:
    'backdrop-blur-xl bg-surface/80 dark:bg-surface/70 border border-white/10 dark:border-primary/10 shadow-card',
};

const paddings: Record<CardPadding, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-5',
  lg: 'p-7',
};

/**
 * Surface container — replaces the many ad-hoc card <div>s.
 * variant: elevated (default) | outlined | plain. When `interactive`, the card
 * gains a pointer cursor and a subtle hover lift (motion `y: -2` + shadow bump).
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ variant = 'elevated', interactive = false, padding = 'md', className, ...props }, ref) => {
    const classes = cn(base, variants[variant], paddings[padding], className);

    if (interactive) {
      return (
        <motion.div
          ref={ref}
          whileHover={{ y: -2 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          className={cn(
            classes,
            'cursor-pointer transition-shadow duration-200 hover:shadow-card-hover',
          )}
          {...(props as React.ComponentPropsWithoutRef<typeof motion.div>)}
        />
      );
    }

    return <div ref={ref} className={classes} {...props} />;
  },
);

Card.displayName = 'Card';
